import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { i18n } from "../main";
import store from "@/store";

export const optionsCurrency = {
  locale: "pt-BR",
  prefix: "R$",
  suffix: "",
  length: 9,
  precision: 2,
};

export const optionsPercent = {
  locale: "pt-BR",
  prefix: "%",
  suffix: "",
  length: 5,
  precision: 2,
};

export const languages = () => {
  return [
    { title: i18n.t("common.pt"), value: "pt_BR" },
    { title: i18n.t("common.en"), value: "en_US" },
    { title: i18n.t("common.es"), value: "es_ES" },
  ];
};

export const filtredSalesChannel = () => {
  return [
    {
      key: "ECOMMERCE;IFRAME;LINK",
      value: i18n.t("events.web-sale"),
    },
    {
      key: "PDV_POS",
      value: i18n.t("events.pos-sale"),
    },
    {
      key: "PDV_POS;ECOMMERCE;IFRAME;LINK",
      value: i18n.t("events.pos-web-sale"),
    },
  ];
};

export function getTextLocale(locale) {
  const normalizedLocale = locale.replace("-", "_");
  const language = languages()?.find(lang => lang.value === normalizedLocale);
  return language ? language.title : "";
}

export const formatCurrency = str => {
  if (!str) return "R$ 0,00";

  return Number(str).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatDate = date => {
  if (!date) return "";
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  return isValid(parsedDate) ? format(parsedDate, "P", { locale: ptBR }) : "";
};

export const formatHour = time => {
  if (time && typeof time === "string" && /^\d{2}:\d{2}:\d{2}$/.test(time)) {
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}`;
  }

  return "";
};
export const formatDateTime = datetime => {
  const data = new Date(datetime);

  return datetime ? format(data, "dd/MM/yyyy - HH:mm") : "";
};

export const getTranslatedText = text => {
  const locale = i18n.locale;

  const extractText = (text, tag1, tag2) => {
    const regex1 = new RegExp(`<${tag1}>(.*?)</${tag1}>`);
    const regex2 = new RegExp(`<${tag2}>(.*?)</${tag2}>`);

    const match1 = text?.match(regex1);
    const match2 = text?.match(regex2);

    return match1 ? match1[1] : match2 ? match2[1] : "";
  };

  const pt = extractText(text, "pt_BR", "pt-BR");
  const en = extractText(text, "en_US", "en-US");
  const es = extractText(text, "es_ES", "es-ES");

  if (locale === "pt_BR" && !pt) {
    return en || es;
  } else if (locale === "en_US" && !en) {
    return pt || es;
  } else if (locale === "es_ES" && !es) {
    return pt || en;
  }

  return locale === "pt_BR" ? pt : locale === "en_US" ? en : es;
};

export function getSlug(multiLangString, language, currentRoute) {
  const regex = new RegExp(`<${language}>(.*?)</${language}>`, "i");
  const match = multiLangString.match(regex);

  if (match) {
    let slug = match[1]
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[/?&#=“<>\\^{}|%@:~+!,–'*();]+/g, "")
      .replace(/-+/g, "-");

    if (currentRoute && currentRoute.includes("duplicar-evento")) {
      slug += `-${i18n.t("copy")}`;
    }
    return slug;
  }

  return null;
}

export function processEventName(name) {
  const nameRegex = /<([a-zA-Z-_]+)>(.*?)<\/\1>/g;
  let match;
  const locales = {};
  while ((match = nameRegex?.exec(name)) !== null) {
    if (match[2].trim()) {
      locales[match[1]] = match[2];
    }
  }
  if (Object.keys(locales).length === 0) {
    return {};
  }
  return locales;
}

function formatDateToAdjust(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function adjustTime(timestamp, hoursToAdd) {
  const date = new Date(timestamp);
  date.setHours(date.getHours() + hoursToAdd);
  return formatDateToAdjust(date);
}

export function extractAndRemoveLocale(currentEvent) {
  const matches = currentEvent?.descricao?.match(/<([^>]+)>(.*?)<\/\1>/g);
  const localesArray = [];
  matches?.forEach(match => {
    const parts = match?.match(/<([^>]+)>(.*?)<\/\1>/);
    const locale = parts[1];
    localesArray?.push(locale);
  });
  const filteredLocales = localesArray?.filter(
    locale => locale !== i18n.locale
  );
  return filteredLocales;
}

export function convertObjectToArray(imagensObject) {
  if (!imagensObject || typeof imagensObject !== "object") {
    return [];
  }
  const result = Object.entries(imagensObject).map(([type, url]) => ({
    url: url,
    type: type,
  }));
  const hasMapImage = result.some(image => image.type === "map");
  const hasEmphasisImage = result.some(image => image.type === "destaque");
  const hasCardImage = result.some(image => image.type === "imagem1");
  if (hasMapImage && !hasEmphasisImage && !hasCardImage) {
    store.commit("setTypeImageSelected", "imagem1");
  } else if (hasEmphasisImage && !hasCardImage) {
    store.commit("setTypeImageSelected", "imagem1");
  } else if (hasCardImage && !hasEmphasisImage) {
    store.commit("setTypeImageSelected", "destaque");
  } else if (hasEmphasisImage && hasCardImage) {
    store.commit("setTypeImageSelected", "map");
  }
  return result;
}
export const normalizeString = text => {
  return text.replace(/\r|['"]+/g, "") || text;
};

export const validatePasswordMinCharacters = password => {
  return password.length < 8;
};

export const validatePasswordMinNumbers = password => {
  return !/\d/.test(password);
};

export const validatePasswordMinLowerCase = password => {
  return !/[a-z]/.test(password);
};

export const validatePasswordMinUpperCase = password => {
  return !/[A-Z]/.test(password);
};

export const validatePasswordMinSpecialCharacters = password => {
  return !/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/~']/.test(password);
};

export const limitText = (text, limit = 30) => {
  if (text.length > limit) return text.slice(0, limit) + "...";
  return text;
};
